/**
 * RefreshButton — shared "Actualizar" button with consistent styling across all pages.
 *
 * Matches the Dashboard refresh button style:
 *  - flex layout with icon + label
 *  - dual-arc SVG with animate-spin when loading
 *  - btnNeutral styling derived from `dark` prop
 *
 * Usage:
 *   <RefreshButton dark={dark} onClick={() => refetch()} isLoading={isFetching} />
 */
import { useTranslation } from 'react-i18next'

const IcoRefresh = ({ spin }: { spin: boolean }) => (
  <svg
    className={`w-3.5 h-3.5 shrink-0 ${spin ? 'animate-spin' : ''}`}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 7A6 6 0 0112.2 3.8M13 7A6 6 0 011.8 10.2" />
  </svg>
)

interface RefreshButtonProps {
  dark: boolean
  onClick: () => void
  /** Pass `isFetching` from TanStack Query for spin animation while refetching */
  isLoading?: boolean
}

export function RefreshButton({ dark, onClick, isLoading = false }: RefreshButtonProps) {
  const { t } = useTranslation()

  const btnCls = [
    'flex items-center gap-1.5',
    'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer',
    dark
      ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 disabled:opacity-40'
      : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm disabled:opacity-40',
  ].join(' ')

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      className={btnCls}
      title={t('common.refresh')}
    >
      <IcoRefresh spin={isLoading} />
      {t('common.refresh')}
    </button>
  )
}
